import { useRef, createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { useBlocker as useRouterBlocker } from 'react-router';

const BlockerContext = createContext();

export function BlockerProvider({ children }) {
  const blockers = useRef([]);
  const rr_blocker = useRef(null);
  
  rr_blocker.current = useRouterBlocker((args) => {
    if (args.historyAction == "REPLACE") {
      return false;
    }
    let top_priority_blocker = blockers.current.sort((a, b) => b.priority - a.priority)[0] || null;
    if (top_priority_blocker) {
      let continue_blocking = top_priority_blocker.callback({ proceed: () => {
        proceed({ id: top_priority_blocker.id });
      } });
      if (!continue_blocking) {
        blockers.current = blockers.current.filter(blocker => blocker.id !== top_priority_blocker.id);
      }
      return true;
    } else {
      return false;
    }
  });

  function proceed({ id }) {
    blockers.current = blockers.current.filter(blocker => blocker.id !== id);
    rr_blocker.current?.proceed?.();
  }

  useEffect(() => {
    function handleBeforeUnload(event) {
      let has_before_unload = blockers.current.some(blocker => blocker.beforeunload);
      if (has_before_unload) {
        event.preventDefault();
      }
    }
  
    window.addEventListener("beforeunload", handleBeforeUnload);
  
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, []);

  return (
    <BlockerContext.Provider value={blockers}>
      {children}
    </BlockerContext.Provider>
  );
}

export function useShouldBlock({type = "forms"}) {
  const blockers = useContext(BlockerContext);

  function shouldBlock() {
    let filtered_blockers = blockers.current.filter(blocker => blocker.type == type);
    let top_priority_blocker = filtered_blockers.sort((a, b) => b.priority - a.priority)[0] || null;
    return top_priority_blocker ? true : false;
  }

  return shouldBlock;
}

export function useContinueUnlessBlocked() {
  const blockers = useContext(BlockerContext);

  function continueUnlessBlocked(proceed) {
    let top_priority_blocker = blockers.current.sort((a, b) => b.priority - a.priority)[0] || null;
    if (top_priority_blocker) {
      let continue_blocking = top_priority_blocker.callback({ proceed: proceed });
      if (!continue_blocking) {
        blockers.current = blockers.current.filter(blocker => blocker.id !== top_priority_blocker.id);
      }
    } else {
      proceed();
    }
  }

  return continueUnlessBlocked;
}

// id must be unique. if not provided, block and unblock don't do anything.
// higher priority is run first
// beforeunload = true prevents closing the tab while blocker is active
// type = "forms", "navigate", or "modals", allows useShouldBlock() to filter out modals
// callback should return true to continue blocking. otherwise blocking stops after one time.
// block_by_default = true blocks by default. if false, block() must be called to block.
export function useBlocker({ id, priority, beforeunload = false, type, callback, block_by_default = false }) {
  const blockers = useContext(BlockerContext);

  const block = useCallback(() => {
    if (id) {
      blockers.current = blockers.current.filter(blocker => blocker.id !== id);
      blockers.current.push({ id, priority, beforeunload, type, callback });
    }
  }, [id, priority, beforeunload, type, callback, blockers]);

  const unblock = useCallback(() => {
    if (id) {
      blockers.current = blockers.current.filter(blocker => blocker.id !== id);
    }
  }, [id, blockers]);

  if (id && (blockers.current.some(blocker => blocker.id == id) || block_by_default)) {
    block();
  }

  // Unblock on component unmount
  useEffect(() => {
    return () => {
      unblock();
    }
  }, [id, unblock]);

  // Use useMemo to create a stable object reference
  return useMemo(() => ({ block, unblock }), [block, unblock]);
}